import { api } from "./client";
import { ep } from "./endpoints";
import { NetworkError } from "./errors";
import type {
  AccessLevel,
  Attachment,
  AttachmentKind,
  RightsLicense,
  UploadSignature,
} from "./types";
import { tr } from "../i18n/strings";
import { makeDerivatives } from "../features/media/derivatives";
import { seed, type MediaVariant } from "../features/media/mediaCache";

export interface UploadArgs {
  pieceId: string;
  kind: AttachmentKind;
  file: File;
  caption?: string;
  /** Recorded on the hero image at creation time; see AttachmentUpdateSerializer. */
  rights?: RightsLicense | "";
  rightsNote?: string;
  accessLevel?: AccessLevel;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * POSTs the bytes straight to object storage under a policy the API signed.
 *
 * XHR rather than fetch purely for `upload.onprogress`: a 200 MB video with no
 * progress bar reads as a frozen app.
 */
function putToStorage(
  signature: UploadSignature,
  blob: Blob,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    // Policy fields must precede the binary; S3 ignores anything after `file`.
    for (const [key, value] of Object.entries(signature.upload.fields)) {
      form.append(key, value);
    }
    form.append("file", blob);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", signature.upload.url, true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new NetworkError(tr().media.uploadFailed));
    xhr.onerror = () => reject(new NetworkError(tr().media.uploadFailed));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));

    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(form);
  });
}

/**
 * sign → upload → thumbnail → confirm.
 *
 * The piece must already exist: `/uploads/sign` scopes the object key to a
 * piece id, so a new record is created first and its media queued after.
 */
export async function uploadAttachment({
  pieceId,
  kind,
  file,
  caption,
  rights,
  rightsNote,
  accessLevel,
  onProgress,
  signal,
}: UploadArgs): Promise<Attachment> {
  const signature = await api.post<UploadSignature>(ep.signUpload, {
    piece: pieceId,
    kind,
    variant: "original",
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size_bytes: file.size,
  });

  // Decode while the original is in flight: the resize is main-thread work that
  // does not depend on the upload, and serialising the two parks the bar at 90%
  // for as long as a full-resolution phone photo takes to draw into a canvas.
  const derivedWork = makeDerivatives(file);

  // Reserve the last slice of the bar for the derivatives and the confirm call.
  await putToStorage(signature, file, (f) => onProgress?.(f * 0.9), signal);

  const derived = await derivedWork;

  /** A missing derivative costs bandwidth on the way in, not correctness. */
  const send = async (variant: "thumbnail" | "display", blob: Blob | null) => {
    if (!blob) return null;
    try {
      const derivedSignature = await api.post<UploadSignature>(ep.signUpload, {
        piece: pieceId,
        kind,
        variant,
        filename: `${variant}.webp`,
        content_type: "image/webp",
        size_bytes: blob.size,
      });
      await putToStorage(derivedSignature, blob, undefined, signal);
      return derivedSignature.object_key;
    } catch {
      return null;
    }
  };

  const thumbnailKey = await send("thumbnail", derived?.thumbnail ?? null);
  const displayKey = await send("display", derived?.display ?? null);

  onProgress?.(0.97);

  const attachment = await api.post<Attachment>(ep.attachments(pieceId), {
    kind,
    object_key: signature.object_key,
    thumbnail_key: thumbnailKey,
    display_key: displayKey,
    original_filename: file.name,
    caption: caption ?? "",
    // Omitted rather than sent as the server's own defaults, so a caller that has
    // no rights UI writes nothing and the model defaults stand.
    ...(rights !== undefined && { rights }),
    ...(rightsNote !== undefined && { rights_note: rightsNote }),
    ...(accessLevel !== undefined && { access_level: accessLevel }),
    width: derived?.width ?? null,
    height: derived?.height ?? null,
    duration_ms: derived?.duration_ms ?? null,
  });

  // Seed only variants the server actually confirmed. `send()` can fail
  // silently (oversize display WebP, flaky network); seeding those blobs under
  // "display" while skipping "original" leaves the hero resolving a cache miss
  // for the master and stuck on the blur placeholder.
  //
  // Await the puts: returning before Cache Storage finishes is a race the list
  // usually wins (small thumbnail) and the detail hero usually loses.
  const cacheable: [MediaVariant, Blob | null][] = [
    ["thumbnail", thumbnailKey ? (derived?.thumbnail ?? null) : null],
    ["display", displayKey ? (derived?.display ?? null) : null],
    ["original", !displayKey && file.type.startsWith("image/") ? file : null],
  ];
  await Promise.all(
    cacheable
      .filter((entry): entry is [MediaVariant, Blob] => entry[1] != null)
      .map(([variant, blob]) => seed(attachment.id, variant, blob)),
  );

  return attachment;
}

/** Which server limit applies, so the UI can reject before spending a round trip. */
export function limitBucketFor(file: File): "image" | "video" | "document" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "document";
}

/** The media section, hero and the rest of the shoot alike. Mirrors `files.views.VISUAL_MIME`. */
const VISUAL_MIME: ReadonlySet<string> = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
]);

const ALLOWED_MIME_BY_KIND: Record<AttachmentKind, ReadonlySet<string>> = {
  primary: VISUAL_MIME,
  gallery: VISUAL_MIME,
  condition_report: new Set(["image/jpeg", "image/png", "image/webp", "application/pdf"]),
  authenticity_evidence: new Set(["image/jpeg", "image/png", "image/webp", "application/pdf"]),
};

export function isAllowedFor(kind: AttachmentKind, file: File): boolean {
  const mime = (file.type || "").split(";")[0].trim().toLowerCase();
  return ALLOWED_MIME_BY_KIND[kind].has(mime);
}
